import { ChangeDetectionStrategy, Component, DestroyRef, NgZone, ViewEncapsulation, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import Swal from 'sweetalert2';
import { environment } from '@src/environments/environment';
import { ServicioSesion } from '@app/services/sesion/sesion.service';
import { ContextoBackend } from '@app/models/auth/contexto-backend';

// Se conserva al cerrar sesión para seguir avisando en login y portal en el navegador de un usuario con rol ROOT
const CLAVE_DISPOSITIVO_ROOT = 'aviso_produccion_root';
// Por pestaña: distingue una pestaña nueva de una recarga
const CLAVE_PESTANA_AVISADA = 'aviso_produccion_pestana';
const MINUTOS_SIN_ATENCION = 5;
// Solo para revisar el aviso en local; nunca subir en true
const FORZAR_EN_LOCAL = false;
const ES_PRODUCCION = environment.production || FORZAR_EN_LOCAL;

@Component({
  selector: 'app-aviso-produccion',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './aviso-produccion.component.html',
  styleUrls: ['./aviso-produccion.component.scss'],
})
export class AvisoProduccionComponent {
  private _sesion = inject(ServicioSesion);
  private _zona = inject(NgZone);
  private _destroyRef = inject(DestroyRef);
  private _router = inject(Router);

  private _contextos = toSignal(this._sesion.contextos$, { initialValue: [] as ContextoBackend[] });
  private _dispositivoRoot = signal(this._leerDispositivoRoot());

  readonly mostrar = computed(() => ES_PRODUCCION && this._dispositivoRoot());

  private _ultimaAtencion = Date.now();

  constructor() {
    // Rol ROOT disponible (no importa el contexto activo); sin contextos (sesión cerrada) se conserva lo último conocido
    effect(() => {
      const contextos = this._contextos();
      if (!contextos.length) return;
      const tieneRoot = contextos.some(c => c.tipo === 'global' && c.rol === 'ROOT');
      this._guardarDispositivoRoot(tieneRoot);
      this._dispositivoRoot.set(tieneRoot);
    }, { allowSignalWrites: true });

    effect(() => {
      document.body.classList.toggle('con-cinta-produccion', this.mostrar());
    });

    if (!ES_PRODUCCION) return;

    // Pestaña nueva: se avisa en la primera pantalla donde aplica la alerta (ej. al entrar desde el login)
    this._avisarPestanaNueva();
    this._router.events.pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(() => this._avisarPestanaNueva());
    this._escucharAtencion();
  }

  private _avisarPestanaNueva(): void {
    if (this._leerSesion(CLAVE_PESTANA_AVISADA) || !this._aplicaAlerta()) return;
    this._escribirSesion(CLAVE_PESTANA_AVISADA);
    this._mostrarAlerta();
  }

  // En el login basta la cinta; en el portal y demás pantallas solo con sesión iniciada
  private _aplicaAlerta(): boolean {
    const enLogin = window.location.pathname.split('/').includes('login');
    return this.mostrar() && !enLogin && this._sesion.haySesionActiva();
  }

  private _escucharAtencion(): void {
    const registrar = () => {
      const ahora = Date.now();
      if (document.visibilityState === 'visible' && ahora - this._ultimaAtencion >= MINUTOS_SIN_ATENCION * 60_000) {
        this._mostrarAlerta();
      }
      this._ultimaAtencion = ahora;
    };
    const alOcultar = () => {
      if (document.visibilityState === 'hidden') this._ultimaAtencion = Date.now();
    };
    const eventos = ['mousedown', 'keydown', 'touchstart', 'wheel', 'focus'];

    this._zona.runOutsideAngular(() => {
      eventos.forEach(e => window.addEventListener(e, registrar, { passive: true }));
      document.addEventListener('visibilitychange', alOcultar);
      document.addEventListener('visibilitychange', registrar);
    });
    this._destroyRef.onDestroy(() => {
      eventos.forEach(e => window.removeEventListener(e, registrar));
      document.removeEventListener('visibilitychange', alOcultar);
      document.removeEventListener('visibilitychange', registrar);
    });
  }

  // Espera a que se cierre cualquier otro Swal para no pisarlo
  private _mostrarAlerta(intentos = 20): void {
    if (!this._aplicaAlerta()) return;
    if (Swal.isVisible()) {
      if (intentos > 0) setTimeout(() => this._mostrarAlerta(intentos - 1), 1000);
      return;
    }
    Swal.fire({
      title: 'ESTÁS EN PRODUCCIÓN',
      html: `<div class="aviso-prod-swal__detalle">${window.location.host}</div>`,
      icon: 'warning',
      width: 420,
      timer: 5000,
      timerProgressBar: true,
      showConfirmButton: false,
      customClass: {
        popup: 'aviso-prod-swal',
        title: 'aviso-prod-swal__titulo',
        icon: 'aviso-prod-swal__icono',
        timerProgressBar: 'aviso-prod-swal__progreso',
      },
    });
  }

  private _leerDispositivoRoot(): boolean {
    try { return localStorage.getItem(CLAVE_DISPOSITIVO_ROOT) === '1'; } catch { return false; }
  }

  private _guardarDispositivoRoot(valor: boolean): void {
    try {
      if (valor) localStorage.setItem(CLAVE_DISPOSITIVO_ROOT, '1');
      else localStorage.removeItem(CLAVE_DISPOSITIVO_ROOT);
    } catch { /* almacenamiento bloqueado */ }
  }

  private _leerSesion(clave: string): boolean {
    try { return sessionStorage.getItem(clave) === '1'; } catch { return false; }
  }

  private _escribirSesion(clave: string): void {
    try { sessionStorage.setItem(clave, '1'); } catch { /* almacenamiento bloqueado */ }
  }
}
